  import { Button } from '@ui/buttons/button';
  import { Trans } from '@ui/i18n/trans';
  import { Form } from '@ui/forms/form';
  import { Fragment, useEffect, useRef, useState, useMemo } from 'react';
  import { DefaultMetaTags } from '@common/seo/default-meta-tags';
  import { Helmet } from '@common/seo/helmet';
  import { CheckCircleOutlineIcon } from '@ui/icons/material/CheckCircleOutline';
  import { ArrowLeftIcon } from '@ui/icons/material/ArrowLeftIcon';
  import { ArrowRightIcon } from '@ui/icons/material/ArrowRightIcon';
  import { BillingCycleRadio } from '@common/billing/pricing-table/billing-cycle-radio';
  import { PricingTable } from '@common/billing/pricing-table/pricing-table';
  import { UpsellBillingCycle } from '@common/billing/pricing-table/find-best-price';
  import { useProducts } from '@common/billing/pricing-table/use-products';
  import { FacebookIcon } from '@ui/icons/social/facebook';
  import { InstagramIcon } from '@ui/icons/social/instagram';
  import { YoutubeIcon } from '@ui/icons/social/youtube';
  import {
    ContactPagePayload,
    useSubmitContactForm,
  } from '@common/contact/use-submit-contact-form';
  import { useForm } from 'react-hook-form';
  import { Dialog } from '@ui/overlays/dialog/dialog';
  import { DialogBody } from '@ui/overlays/dialog/dialog-body';
  import { DialogHeader } from '@ui/overlays/dialog/dialog-header';
  import { DialogTrigger } from '@ui/overlays/dialog/dialog-trigger';
  import { FormTextField } from '@ui/forms/input-field/text-field/text-field';
  import { useRecaptcha } from '@common/recaptcha/use-recaptcha';
  import { LandingNavbar } from '@app/landing/landing-navbar';
  import '@app/landing/landing-page.css';
  import { FullPageLoader } from '@ui/progress/full-page-loader';
  import { LucideIcon } from "lucide-react";
  // import { FaEnvelope, FaPhoneAlt, FaMapMarkerAlt  } from "react-icons/fa";
  import { motion } from "framer-motion";
  import { Globe, Lock, Database,Briefcase , History , FileCheck, Users, Building2 , GraduationCap , Folder, UserCog, RefreshCw, Share2, UserPlus, Upload, Globe2 } from 'lucide-react';
  import { FolderKanban, Sparkles, Zap, Clock, KeyRound, Eye, Shield } from 'lucide-react';
  import {
  Tag, SlidersHorizontal, Workflow,Server,
  HardDrive,
  ShieldCheck,
  Megaphone,
  PenTool,
} from "lucide-react";

  import { useThemeSelector } from '@ui/themes/theme-selector-context';


  export function Enterprise() {

    const query = useProducts('pricingPage');
    const [selectedCycle, setSelectedCycle] =
      useState<UpsellBillingCycle>('month');

    const { selectThemeTemporarily } = useThemeSelector();
    useEffect(() => {
      selectThemeTemporarily('light');
      return () => selectThemeTemporarily(null);
    }, [selectThemeTemporarily]);

    useEffect(() => {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
    }, []);

    const steps = [
      {
        icon: UserPlus,
        number: '01',
        title: 'Sign Up',
        description: 'Create your account in seconds with no credit card required',
      },
      {
        icon: Upload,
        number: '02',
        title: 'Upload Files',
        description: 'Upload files or sync folders instantly with secure, high-speed cloud storage',
      },
      {
        icon: Globe2,
        number: '03',
        title: 'Access Anywhere',
        description: 'Access your data from any device, anywhere in the world',
      },
    ];

    const [openIndex, setOpenIndex] = useState<number | null>(0);

    const toggle = (index: number) => {
      setOpenIndex(openIndex === index ? null : index);
    };

    const faqData = [
      {
        question: "Does SpaceByte offer enterprise data residency in India?",
        answer: "Yes, strict <strong>enterprise data residency</strong> is a core feature of SpaceByte. As the leading <strong>enterprise cloud storage provider India</strong> relies on, we deliver a 100% <strong>sovereign enterprise cloud</strong>. For large teams needing <strong>secure enterprise cloud storage</strong> and robust <strong>enterprise file storage</strong>, SpaceByte ensures full compliance with the DPDP Act."
      },
      {
        question: "What is scalable cloud data storage?",
        answer: "Scalable cloud data storage refers to the practice of storing digital information on remote, distributed servers that can dynamically allocate resources. Unlike localized, on-premises hardware, a scalable cloud allows businesses to seamlessly increase or decrease storage capacity on demand (cloud elasticity) without purchasing new physical infrastructure."
      },
      {
        question: "What is the difference between on-premises and cloud storage?",
        answer: "On-premises storage requires dedicated physical servers owned and maintained by the organization, resulting in high CapEx and maintenance. Cloud storage uses remote servers managed by a provider, offering flexibility, lower OpEx, and scalable elasticity without hardware limits."
      },
      {
        question: "How do you evaluate enterprise cloud storage solutions?",
        answer: "Modern <strong>enterprise cloud storage solutions</strong> require advanced security architectures. When comparing <strong>public vs private vs hybrid cloud options</strong>, SpaceByte stands out by offering strict <strong>role-based access control (RBAC)</strong> and seamless <strong>automated disaster recovery</strong>, ensuring complete data resilience for large organizations.",
      },
      {
        question: "Why do enterprises need cloud storage?",
        answer:
          "Enterprises deal with large amounts of data every day. Cloud storage makes it easier to store, organize, and access files securely without relying on physical systems.",
      },
      {
        question: "Can employees access files remotely?",
        answer:
          "Yes, SpaceByte cloud storage allows employees to access files from any device or location, making it ideal for remote teams and flexible work environments.",
      },
      {
        question: "Can SpaceByte cloud storage handle large enterprise files?",
        answer:
          "Yes, enterprise cloud storage is designed to support large files like videos, backups, and datasets without performance issues.",
      },
      {
        question: "How does cloud storage improve workflow?",
        answer:
          "It reduces time spent searching for files, keeps everything organized, and ensures teams always work on the latest version of documents.",
      },
      {
        question: "How do you achieve scalable cloud data storage?",
        answer:
          "Achieving <strong>scalable cloud data storage</strong> requires leveraging true <strong>cloud elasticity</strong>. Instead of rigid servers, modern architectures allow you to utilize <strong>horizontal vs vertical scaling in cloud</strong> environments instantly, which is one of the core <strong>enterprise cloud scaling strategies</strong>.",
      },
      {
        question: "What are the best practices for data storage in the cloud?",
        answer:
          "Following the <strong>best practices for data storage in the cloud</strong> means understanding modern <strong>cloud storage technologies</strong>. You must evaluate <strong>object storage vs block storage</strong> to match your specific performance workloads and ensure high-availability redundancy.",
      },
      {
        question: "What is cloud storage architecture in cloud computing?",
        answer: "The <strong>cloud storage architecture</strong> defines how data is intelligently distributed across networks. In enterprise environments, the <strong>cloud storage system architecture</strong> ensures high availability and failover resilience. <strong>Cloud storage architecture in cloud computing</strong> relies on clustered servers that automatically replicate data. While a <strong>cloud storage architecture diagram</strong> can look complex, it simply represents the seamless abstraction of physical servers into virtual storage pools."
      },
      {
        question: "How does cloud storage compare to on-premise data centers?",
        answer: "When analyzing <strong>cloud storage vs on premise</strong> or <strong>cloud storage vs data center</strong>, the key difference is CapEx versus OpEx. <strong>Cloud storage vs cloud computing</strong> are closely related concepts, where storage serves as the foundational data layer. Enterprises transitioning from legacy systems leverage a <strong>cloud storage transfer service</strong> to securely migrate to modern <strong>cloud storage types in cloud computing</strong> without experiencing downtime."
      }
    ];

    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [visible, setVisible] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    const reviews = useMemo(
      () => [
        {
          name: "Rahul Mehta",
          role: "  Operations Manager",
          review: "Managing large files used to be messy for our team, especially with multiple departments involved. After switching to SpaceByte, everything feels more organized and easier to access. It honestly saves us a lot of time every day.",
          avatar: "",
          rating: 5,
          accent: "#2563EB",
          light: "#EAF2FF",
        },
        {
          name: "Anjali Sharma",
          role: "Marketing Lead",
          review: "We deal with a lot of media files, and storage was always a challenge. SpaceByte handles large files smoothly, and sharing them with the team is much faster than before.",
          avatar: "",
          rating: 4,
          accent: "#7C3AED",
          light: "#F3E8FF",
        },
        {
          name: "Sneha Iyer",
          role: "Marketing Manager",
          review: "Our team is spread across different locations, and this has made collaboration much simpler. Everyone can access what they need without constant follow-ups, which saves a lot of time.",
          avatar: "",
          rating: 4,
          accent: "#0284C7",
          light: "#E0F2FE",
        },
        {
            name: "Rajiv Malhotra",
            role: "Project Lead",
            review: "Earlier, we spent too much time searching for files. Now everything is organized properly, and it has improved our daily workflow more than we expected.",
            avatar: "",
            rating:3,
            accent: "#2563EB",
            light: "#EAF2FF",
        },
      ],
      []
    );


  const total = reviews.length;

    useEffect(() => {
      const obs = new IntersectionObserver(
        ([e]) => {
          if (e.isIntersecting) setVisible(true);
        },
        { threshold: 0.12 }
      );

      if (ref.current) obs.observe(ref.current);
      return () => obs.disconnect();
    }, []);

    const prev = () => setActiveIndex((p) => (p === 0 ? total - 1 : p - 1));
    const next = () => setActiveIndex((p) => (p === total - 1 ? 0 : p + 1));

  
    const cardWidth = 450; // desktop reference
    const gap = 24;

    // Offset only for desktop logic
    const offset = activeIndex * (cardWidth + gap);

 const features: {
      icon: LucideIcon;
      title: string;
      text: string;
    }[] = [
    {
      icon: Workflow,
      title: "Smooth Workflow",
      text: "With organized and reliable storage, teams can work more efficiently without confusion or interruptions.",
    },
    {
      icon: Briefcase,
      title: "Easy Project Management",
      text: " Teams are able to manage projects, collaborate on files, and share resources with fewer delays.",
    },
    {
      icon: Database,
      title: "Centralized File Access",
      text: "All business files are kept in one place, making them easier to access and manage.",
    },
    {
      icon: Users,
      title: "Ideal for Remote Teams",
      text: " Works well for remote teams, marketing departments, and project managers handling multiple responsibilities.",
    },
    {
      icon: Clock,
      title: "Saves Time",
      text: "Cuts down the time spent searching for files and important documents.",
    },
    {
      icon: SlidersHorizontal,
      title: "Better File Control",
      text: "Makes sure everyone is working on the most recent and updated version of files.",
    },
  ];

  interface Role {
    num: string;
    icon: LucideIcon;
    title: string;
    desc: string;
  }

  const ROLES: Role[] = [
    { num: "01", icon: Server , title: "Secure Data Protection", desc: " Advanced security measures help protect sensitive company data and prevent unauthorized access." },
    { num: "02", icon: Globe , title: "Large File Support", desc: " Enterprises often manage high-resolution media files, project backups, and heavy datasets. A scalable platform allows smooth uploads and storage of large files." },
    { num: "03", icon: Users , title: "Team Collaboration Tools", desc: "Files can be shared easily between departments, improving communication and reducing delays in project workflows." },
    { num: "04", icon: HardDrive, title: "Access from Anywhere", desc: " Employees can securely reach important documents from any device, making remote work and collaboration much easier." },
    { num: "05", icon: ShieldCheck, title: "Scalable Storage Infrastructure", desc: "As companies grow, their data grows too. Flexible storage ensures organizations never run out of space when they need it most." },
  ];

  function useReveal() {
    useEffect(() => {
      const obs = new IntersectionObserver(
        (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
        { threshold: 0.08 }
      );
      document.querySelectorAll(".reveal").forEach((el) => obs.observe(el));
      return () => obs.disconnect();
    }, []);
  }

    const styleInjected = useRef(false);
  
    // Inject global CSS once
    useEffect(() => {
      if (styleInjected.current) return;
      styleInjected.current = true;
      const tag = document.createElement("style");
      tag.innerHTML = GLOBAL_CSS;
      document.head.appendChild(tag);
    }, []);
  
    useReveal();

          const GLOBAL_CSS = `

      :root {
        --bg:       #F5F2EC;
        --white:    #FDFCFA;
        --ink:      #1C1B18;
        --ink2:     #3A3830;
        --muted:    #8C8880;
        --line:     rgba(28,27,24,0.12);
        --mint:     #2DC88A;
        --mint-dim: rgba(45,200,138,0.12);
        --rust:     #E05A2B;
        --sky:      #1A6FFF;
      }
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      html { scroll-behavior: smooth; }
      body { font-famaliy: font-archivo; }

      /* ROLES */
      .sb-roles { display: grid; grid-template-columns: 280px 1fr; border-bottom: 2px solid #1E3A8A; border-top: 2px solid #1E3A8A; min-height: 420px; }
      .sb-roles-left { border-right: 2px solid #1E3A8A; padding: 3.5rem 2.5rem; background: #1E3A8A; color: var(--bg); display: flex; flex-direction: column; justify-content: space-between; }
      .sb-roles-eyebrow { font-size: .7rem; font-weight: 700; text-transform: uppercase; letter-spacing: .14em; color: #00c2ff; margin-bottom: 1.2rem; }
      .sb-roles-left h2 { font-size: 2rem; font-weight: 900; line-height: 1.1; letter-spacing: -.5px; }
      .sb-roles-left p { font-size: .85rem; color: rgba(253,252,250,0.55); margin-top: 1rem; line-height: 1.65; }
      .sb-rl-count { font-size: 3.5rem; font-weight: 900; color: #00c2ff; line-height: 1; }
      .sb-roles-list { display: flex; flex-direction: column; }
      .sb-role-row { display: flex; align-items: center; border-bottom: 1px solid var(--line); cursor: pointer; transition: background .2s; }
      .sb-role-row:last-child { border-bottom: none; }
      .sb-role-row:hover { background: #e3eaf9; }
      .sb-role-row:hover .sb-rr-arrow { opacity: 1; transform: translateX(0); }
      .sb-rr-num { width: 60px; flex-shrink: 0; border-right: 1px solid var(--line); text-align: center; padding: 1.8rem 0; font-size: .75rem; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: var(--muted); }
      .sb-rr-icon {
        width: 64px;
        flex-shrink: 0;
        border-right: 1px solid var(--line);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1.8rem 0;
        font-size: 1.4rem;
      }
      .sb-rr-body { flex: 1; padding: 1.8rem 2rem; }
      .sb-rr-title { font-size: .95rem; font-weight: 700; color: var(--ink); margin-bottom: .25rem; }
      .sb-rr-desc { font-size: .8rem; color: var(--muted); line-height: 1.5; }
      .sb-rr-arrow { padding: 0 1.5rem; font-size: 1.1rem; color: #3B82F6; opacity: 0; transform: translateX(-6px); transition: opacity .2s, transform .2s; }

      /* REVEAL */
      .reveal { opacity: 0; transform: translateY(24px); transition: opacity .6s ease, transform .6s ease; }
      .reveal.visible { opacity: 1; transform: translateY(0); }

      /* RESPONSIVE */
      @media (max-width: 900px) {
        .sb-roles { grid-template-columns: 1fr; }
        .sb-roles-left { border-right: none; border-bottom: 2px solid var(--ink); }
        .sb-stats { grid-template-columns: 1fr 1fr; }
      }
      @media (max-width: 600px) {
        .sb-nav-center { display: none; }
        .sb-stats { grid-template-columns: 1fr; }
        .sb-stat-cell { border-right: none; border-bottom: 1px solid var(--line); }
      }
      `;
      
    return (
      <Fragment>
          <Helmet>
          <title>Scalable Cloud Storage for Enterprise</title>
          <meta
            name="description"
            content="Cloud storage for enterprise with scalable solutions, advanced security, and seamless team collaboration. Perfect for large and growing businesses."
          />
          <meta name="keywords" content="Cloud Storage for Enterprise" />
          <link rel="canonical" href="https://spacebyte.in/cloud-storage-for-enterprise" />
        </Helmet>

        <LandingNavbar
          color="transparent"
          className="fixed top-0 z-50 w-full flex-shrink-0 border-none bg-light-primary shadow-lg"
          menuPosition="homepage-navbar"
        />

      <section className=" relative overflow-hidden !bg-gradient-to-r !from-[#0072ff]/8 !to-[#00c6ff]/8 py-24 md:py-32">
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1E3A8A" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        <div
          className={`landing-container z-10 flex flex-col items-center justify-between pb-40 pt-100 lg:flex-row`}
        >
          <div className="mx-auto w-full max-w-6xl px-4 text-center">

            {/* Heading */}
            <h1 className="font-archivo text-[40px] md:text-[60px] font-bold leading-[1.2] tracking-[-0.02em] text-[#1f2937]">
              Enterprise Cloud Storage   {" "} 
              <br />  
              for  <span className="color-primary-gradient"> Modern Businesses  </span>
              <br />

            </h1>

            <p className="mx-auto mt-30 max-w-3xl font-inter text-[18px] md:text-[20px]  text-[#6b7280]">
              Modern enterprises need secure cloud storage to manage large volumes of files, improve team collaboration, protect data, and ensure easy access without storage limits or risk.
            </p>

          <div className="my-36 pb-20 flex flex-col items-center justify-center gap-6 sm:flex-row">
            <a
              href="/register"
              className="inline-flex items-center justify-center 
              px-26 py-10 
              text-white font-bold
              bg-gradient-to-r from-[#0072ff] to-[#00c6ff]
              shadow-lg transition-all duration-300
              hover:from-[#0059d6] hover:to-[#00aaff]
              hover:shadow-2xl hover:-translate-y-1"
              style={{
              borderRadius: "12px"
            }}
            >
              Get 25 GB FREE
            </a>
            <a
              href="/pricing"
              className="inline-flex items-center justify-center
              px-36 py-8 font-bold
              text-[#0072ff]
              relative transition-all duration-300
              hover:-translate-y-1 hover:shadow-lg "
              style={{
                borderRadius: "12px",
                background: "linear-gradient(#e5f5fb, #e5f5fb) padding-box, linear-gradient(90deg, #0072ff, #00c6ff) border-box",
                border: "2px solid transparent"
              }}
              >
              View Pricing
            </a>
          </div>
          </div>
        </div>
      </section>   

      <section className="py-24 md:py-32 bg-gradient-to-br from-[#F8FAFC] to-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 landing-container z-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-[#3B82F6] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#06B6D4] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 my-30 md:my-50 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-archivo font-bold text-[#111827] mb-10">
              Start Your Cloud Storage in <span className="color-primary-gradient">Three Steps</span>{" "}

            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto pb-10 font-archivo">
              Start storing and sharing your files securely in minutes
            </p>
          </motion.div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative landing-container">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-90 left-0 right-0 h-2 bg-gradient-to-r from-[#3B82F6] via-[#60A5FA] to-[#06B6D4] opacity-30"></div>

            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative"
              >
                <div className="flex flex-col items-center text-center">
                  {/* Icon Circle */}
                  <div className="relative mb-6">
                    <div className="w-80 h-80 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-full flex items-center justify-center relative z-10 shadow-lg">
                      <step.icon className="w-40 h-40 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-10  w-40 h-40 bg-white rounded-full flex items-center justify-center border-2 border-[#3B82F6] z-20">
                      <span className="text-[#1E3A8A] font-bold text-sm">{step.number}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-semibold font-archivo text-[#111827] my-20">
                    {step.title}
                  </h3>
                  <p className="text-[#6B7280] font-archivo max-w-xs">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Animated Line */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, delay: 0.5 }}
            className=" hidden md:block absolute top-[260px] left-[25%] right-[25%] h-3 bg-gradient-to-r from-[#3B82F6] to-[#06B6D4] rounded-full origin-left"
          ></motion.div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#f5f8ff] py-40 text-black lg:py-50">
        <div className="landing-container z-10 flex flex-col items-center justify-between gap-8 px-4 md:px-8 lg:flex-row">
          {/* Left Section */}
          <div className="relative w-full lg:w-1/2 flex justify-center">
            <div className="relative w-full max-w-[520px]">

              {/* Background Offset Block */}
              <div className="absolute -bottom-4 -right-4 h-full w-full rounded-2xl bg-gradient-to-br from-blue-600 to-blue-400" />

              {/* Image */}
              <img
                src="/images/enterprise/enterprise.webp"
                alt="cloud storage for enterprise"
                className="relative rounded-2xl shadow-2xl w-full object-cover"
              />
            </div>
          </div>

          {/* Right Content */}
          <div className="w-full space-y-16 lg:w-1/2 pt-14 md:pt-0">
            <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
             The New Data Era for  <span className="color-primary-gradient">Enterprises </span>
            </h2>
            <p className="color-gray font-inter animate-fade-down">
              Data drives modern enterprises, but managing large volumes with traditional systems can lead to security risks and inefficiencies. Cloud storage for enterprise provides a secure, centralized solution where teams can access, share, and manage files easily, improving collaboration while protecting important data from loss or hardware failures.
            </p>
          </div>
        </div>
      </section>
              
      <section
        id="Pricing"
        className="pricing-section relative overflow-hidden bg-white py-40 text-black lg:py-50"
      >
        <div className="landing-container z-10">
          <div className="mx-auto max-w-xl space-y-16 text-center">
            <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
              Affordable <span className="color-primary-gradient"> Cloud Storage Pricing </span> Plans
            </h2>
            <p className="color-gray font-inter animate-fade-down">
              Get affordable pricing with no hidden fees, encrypted cloud storage,
              and fast, seamless access to your files anytime, anywhere.
            </p>
          </div>

          <div className="my-40 flex flex-col md:flex-row justify-center items-center gap-16">
            <BillingCycleRadio
              products={query.data?.products}
              selectedCycle={selectedCycle}
              onChange={setSelectedCycle}
              className="input-radio animate-fade-up flex justify-center items-center flex-wrap"
              size="lg"
            />
          </div>

          <PricingTable
            selectedCycle={selectedCycle}
            productLoader="pricingPage"
            className="animate-fade-down"
          />

          {/* Premium Section */}
          <div
            className="premium-bg animate-fade-down bg-light-primary relative mt-40 flex flex-col items-center justify-between space-y-8 overflow-hidden rounded-2xl border-primary p-16 shadow-lg md:flex-row md:space-y-0 md:p-32"
            style={{
              backgroundImage:
                "url('/images/LandingImg/ctamask.png'), url('/images/LandingImg/ctashape.png')",
              backgroundSize: 'contain, contain',
              backgroundRepeat: 'no-repeat, no-repeat',
              backgroundPosition: 'left center, right center',
            }}
          >
            {/* Premium Tag */}
            <div className="font-archivo absolute -left-54 top-16 w-160 rotate-[-45deg] transform rounded bg-warning text-center text-sm font-medium text-black md:-left-56 lg:-left-50 lg:top-20">
              Premium
            </div>

            {/* Icon and Text */}
            <div className="flex flex-col items-center gap-8 space-y-8 md:flex-row md:space-x-8 md:space-y-0">
              <img
                src="/images/LandingImg/cta-icon.webp"
                className="color-primary rounded-lg bg-white p-16"
              />
              <div className="text-center md:text-left">
                <h2 className="font-archivo text-3xl font-bold">
                  5 TB or More?
                </h2>
                <span className="color-primary font-archivo text-2xl font-semibold">
                  ₹0.15/GB{' '}
                  <span className="text-lg font-normal text-black">
                    /Monthly
                  </span>
                </span>
                <p className="font-inter mt-8 flex items-start justify-center gap-4 text-sm md:justify-start">
                  <CheckCircleOutlineIcon size="sm" /> 100% Safe & Secure with
                  End-to-End Encryption
                </p>
                <p className="font-inter mt-1 flex items-start justify-center gap-4 text-sm md:justify-start">
                  <CheckCircleOutlineIcon size="sm" /> Calculate the cost or
                  Reach out to us
                </p>
                <p className="font-inter mt-4 text-sm text-gray-700 max-w-md hidden md:block">
                  SpaceByte Cloud Storage is a scalable enterprise platform designed for massive data needs of 5 TB or more. Our secure infrastructure handles high-volume backups seamlessly and affordably, offering custom storage plans for heavy business workloads that guarantee zero-knowledge encryption and uninterrupted access.
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-row flex-wrap gap-8 sm:flex-col lg:flex-row justify-center">
              <div className="flex justify-center">
                {CalculateDialogTrigger()}
              </div>

              <div className="mt-0 flex justify-center">
                <a href="/contact" className="btn bg-[#f9fafb] border-2 border-[#0072ff] text-black hover:bg-[#0072ff] hover:text-white">
                  <span>Contact Us</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#f5f8ff] py-[80px] relative overflow-hidden ">
      <div className="landing-container z-10 max-w-[1140px] mx-auto px-[20px]">
        {/* Header */}
          <div className="text-center mb-14">
              <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
              Smart Storage for { " " }
                <span className="color-primary-gradient"> Enterprise Teamse</span>
              
              </h2>
              <p className="color-gray font-inter animate-fade-down py-16 md:py-24">
                SpaceByte smart cloud storage helps enterprise teams stay organized, collaborate more efficiently, and access files without unnecessary delays, making everyday workflows smoother and more productive.
              </p>
          </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[24px] mt-[52px]">
          {features.map((item, index) => (
            <WhyCard
              key={index}
              item={item}
            />
          ))}
        </div>
      </div>
      </section>

      <section className="py-24 md:py-32 relative overflow-hidden bg-[#f5f8ff]">
      <div className="sb-roles landing-container z-10" id="roles">
        <div className="sb-roles-left">
          <div>
            {/* <div className="sb-roles-eyebrow">Who It&apos;s For</div> */}
            <h2>Enterprise Features Built for Growing Organizations</h2>
            <p>Modern enterprises require more than just storage space. They need a solution designed for performance, security, and flexibility.</p>
          </div>
          <div className="sb-rl-count">
            5<br />
            <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: ".85rem", fontWeight: 500, color: "rgba(253,252,250,0.45)", letterSpacing: ".08em", textTransform: "uppercase" }}>
              Roles Covered
            </span>
          </div>
        </div>
        <div className="sb-roles-list">
          {ROLES.map((r) => (
            <div className="sb-role-row reveal" key={r.num}>
              <div className="sb-rr-num">{r.num}</div>
              <div className="sb-rr-icon"><r.icon size={28} strokeWidth={2} className="text-[#1E3A8A]" /></div>
              <div className="sb-rr-body">
                <div className="sb-rr-title">{r.title}</div>
                <div className="sb-rr-desc">{r.desc}</div>
              </div>
              <div className="sb-rr-arrow">→</div>
            </div>
          ))}
        </div>
      </div>
      </section>

      <section className=" py-24 md:py-32 relative overflow-hidden bg-[#f5f8ff]" >
        <div className="sb-features landing-container z-10">
          <div className=" sb-fs-header font-archivo pb-20" >
      <div className="text-center space-y-8 ">
        <h3 className="font-archivo text-3xl font-bold md:text-4xl lg:text-5xl text-[#1f2937]">
            Why Businesses Choose   <span className="color-primary-gradient">Reliable Enterprise Storage </span>  
        </h3>
        <p className="font-inter text-sm md:text-base color-gray my-10">
            Enterprises need a platform that not only stores files but also supports productivity and long-term data management. A reliable storage solution offers better performance, stronger security, and simplified file organization
        </p>
        <p className="font-inter text-sm md:text-base color-gray my-10">
           Companies often choose <span className="color-primary-gradient"> cloud storage for business  </span> because it reduces dependence on expensive hardware while providing faster access to files. It also helps IT teams manage data more efficiently without maintaining physical servers.
        </p>
        <p className="font-inter text-sm md:text-base color-gray my-10">
           A well-designed storage environment ensures that important information stays protected while teams can collaborate without limitations.
        </p>
        </div>
        </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-10 py-40 bg-white relative overflow-hidden">
        <div className="landing-container min-h-[280px] md:min-h-[380px] flex items-center justify-center relative rounded-2xl overflow-hidden">

          {/* Video Background */}
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/images/LandingImg/video.mp4"
            autoPlay
            loop
            muted
            playsInline
          />

          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-black/60" />

          {/* Gradient Light */}
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-transparent to-blue-600/20" />

          {/* Content */}
          <div className="relative z-10 text-center max-w-4xl px-4">
            <h2 className="text-white text-3xl md:text-5xl font-semibold">
              Your Data, Your Control, Your Cloud
            </h2>

            <p className="text-white/80 text-base md:text-lg mt-20">
              A smarter cloud storage platform that gives you complete control over your data, file sharing, and digital privacy.

            </p>

            <div className="mt-20">
              <a href="/contact" className="btn bg-white text-black px-20 py-10 rounded-xl font-medium hover:scale-105 transition">
                <span>Contact Us</span>
              </a>
            </div>
          </div>

        </div>
      </section>


      <section id="faq" className="bg-[#f5f8ff] relative overflow-hidden py-24 text-black lg:py-32">
        <div className="landing-container z-10">
          <div className="section-wrapper">
            <div className="section-inner">
              {/* LEFT PANEL */}
              <div className="left-panel">
                <div className="badge font-archivo">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <circle cx="5" cy="5" r="5" fill="#38bdf8" />
                  </svg>
                  SpaceByte Cloud
                </div>

                <h2 className="left-title font-archivo">
                  Best Cloud Storage <br />
                  Secure, Fast and  <br />
                  Reliable
                </h2>

                <p className="left-sub font-archivo">
                  Everything you need to store, share, and access your files anytime, anywhere.
                </p>

                <div className="illustration">
                  <div className="illus-card">
                    <div className="illus-row">
                      <div className="illus-icon ic-blue">☁️</div>
                      <div className="illus-text font-archivo">
                        <strong>My Storage</strong>
                        <span>18.3 GB used of 25 GB</span>
                        <div className="illus-bar">
                          <div className="illus-bar-fill" style={{ width: "73%" }} />
                        </div>
                      </div>
                    </div>

                    <div className="illus-row">
                      <div className="illus-icon ic-sky">📁</div>
                      <div className="illus-text font-archivo">
                        <strong>Shared Folders</strong>
                        <span>12 folders · 3 collaborators</span>
                      </div>
                    </div>

                    <div className="illus-row">
                      <div className="illus-icon ic-white">🔒</div>
                      <div className="illus-text">
                        <strong>End-to-End Encrypted</strong>
                        <span>All files secured in transit</span>
                      </div>
                    </div>

                    <div className="stats-row">
                      <div className="stat-chip">
                        <strong>25 GB</strong>
                        <span>Free Storage</span>
                      </div>
                      <div className="stat-chip">
                        <strong>99.9%</strong>
                        <span>Uptime</span>
                      </div>
                      <div className="stat-chip">
                        <strong>24/7</strong>
                        <span>Support</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT PANEL */}
              <div className="right-panel">
                {/* <p className="section-tag">Help Center</p> */}

                <h2 className="section-title">
                  Quick Reference Guide
                </h2>

                <p className="section-desc">
                  Find answers to the most common questions about SpaceByte Cloud Storage.
                </p>

                <div className="accordion">
                  {faqData.map((item, index) => (
                    <div
                      key={index}
                      className={`acc-item ${openIndex === index ? "open" : ""}`}
                    >
                      <button className="acc-header" onClick={() => toggle(index)}>
                        <div className="acc-num">
                          {String(index + 1).padStart(2, "0")}
                        </div>

                        <span className="acc-label">{item.question}</span>

                        <div className="acc-chevron">
                          <svg viewBox="0 0 24 24" fill="none">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                      </button>

                      <div className="acc-body">
                        <div
                          className="acc-body-inner"
                          dangerouslySetInnerHTML={{ __html: item.answer }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={ref}
        className="relative overflow-hidden bg-white py-30 md:py-50"
      >
        {/* Background Orbs (same as ::before/::after) */}
        <div className="pointer-events-none absolute -left-[180px] -top-[180px] h-[560px] w-[560px] rounded-full bg-[radial-gradient(circle,#dbeafe40,transparent_70%)]" />
        <div className="pointer-events-none absolute -bottom-[160px] -right-[160px] h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,#ede9fe40,transparent_70%)]" />

        <div className="relative z-[1] mx-auto grid max-w-[1280px] grid-cols-2 items-stretch gap-[56px] px-[clamp(20px,5vw,72px)] max-[900px]:grid-cols-1 max-[900px]:gap-[40px]">
          {/* ───────────────── LEFT ───────────────── */}
          <div
            className={[
              "transition-all duration-700 ease-out",
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-7",
            ].join(" ")}
          >


            {/* Heading */}
            <h2 className="my-10 pt-6 md:pt-30 font-archivo text-[clamp(26px,3.2vw,46px)] font-extrabold leading-[1.15] tracking-[-0.025em] text-[#0f172a]">
              What Our {" "}
              <span className="bg-gradient-to-br font-archivo color-primary-gradient">
                Customers Say
              </span>{" "}

            </h2>

            {/* Text */}
            <p className="mb-10  max-w-[440px] font-archivo text-[clamp(14px,1.1vw,16px)] leading-[1.82] text-[#64748b]">
              See how SpaceByte delivers secure file storage, high-speed uploads, and advanced privacy protection. SpaceByte is a cloud storage platform trusted by individuals and businesses to safeguard their most important data. Read our verified customer reviews and testimonials below before making your secure cloud storage decision to see why users love us.
            </p>



            {/* Controls */}
            <div className="flex flex-wrap my-20  items-center gap-3">
              <button
                onClick={prev}
                className="flex h-[50px] w-[50px] items-center justify-center rounded-full border-[1.5px] border-[#e0e7ff] bg-white text-[#2563eb] shadow-[0_2px_12px_rgba(37,99,235,0.09)] transition-all duration-200 ease-out hover:scale-[1.08] hover:border-[#2563eb] hover:bg-[#2563eb] hover:text-white hover:shadow-[0_8px_24px_rgba(37,99,235,0.28)]"
              >
                <ArrowLeftIcon />
              </button>

              <button
                onClick={next}
                className="flex h-[50px] w-[50px] items-center justify-center rounded-full border-[1.5px] border-[#e0e7ff] bg-white text-[#2563eb] shadow-[0_2px_12px_rgba(37,99,235,0.09)] transition-all duration-200 ease-out hover:scale-[1.08] hover:border-[#2563eb] hover:bg-[#2563eb] hover:text-white hover:shadow-[0_8px_24px_rgba(37,99,235,0.28)]"
              >
                <ArrowRightIcon />
              </button>
            </div>

            {/* Dots */}
            <div className="ml-[6px] flex gap-[6px] my-16">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={[
                    "h-[8px] rounded-[4px] transition-all duration-300",
                    i === activeIndex ? "w-[26px] bg-[#2563eb]" : "w-[8px] bg-[#cbd5e1]",
                  ].join(" ")}
                />
              ))}
            </div>

            {/* Progress */}
            <div className="mt-5 h-[3px] max-w-[220px] overflow-hidden rounded bg-[#e2e8f0]">
              <div
                className="h-full rounded bg-gradient-to-r from-[#2563eb] to-[#7c3aed] transition-all duration-500"
                style={{ width: `${((activeIndex + 1) / total) * 100}%` }}
              />
            </div>
          </div>

          {/* ───────────────── RIGHT (SLIDER) ───────────────── */}
          <div
          className={[
            "w-full transition-all duration-700 ease-out delay-200",
            visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-7",
          ].join(" ")}
        >
          <div className="relative w-full overflow-hidden">
            {/* right fade */}
            <div className="pointer-events-none absolute right-0 top-0 z-[2] h-full w-[64px] bg-gradient-to-l from-white to-transparent" />

            <div
              className="flex items-stretch gap-6 transition-transform duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]"
              style={{
                transform: `translateX(-${offset}px)`,
              }}
            >
              {reviews.map((item, i) => (
                <ReviewCard key={i} item={item} isActive={i === activeIndex} />
              ))}
            </div>
          </div>
        </div>
      </div>
      </section>

      <section className="py-20 md:py-32 bg-gradient-to-br from-[#1E3A8A] via-[#3B82F6] to-[#60A5FA] relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 landing-container z-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="cta-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cta-grid)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-40 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="inline-flex items-center justify-center w-60 h-60 bg-white/20 backdrop-blur-sm rounded-2xl mb-12"
            >
              <Sparkles className="w-30 h-30 text-white" />
            </motion.div>

            {/* Heading */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white"
            >
              Ready to Secure Your Data?
            </motion.h2>

            {/* Subheading */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-lg sm:text-xl text-white/90 my-16 max-w-2xl mx-auto"
            >
              SpaceByte Cloud Storage is a comprehensive cloud storage solution that guarantees zero-knowledge encryption for businesses and individuals. Join thousands of users trusting our secure infrastructure to protect critical data, ensuring your files remain private, safe from ransomware, and accessible anytime.
            </motion.p>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-10 justify-center items-center"
            >
              <a href="/register" className="group bg-white text-[#1E3A8A] px-16 py-14 rounded-xl font-semibold hover:bg-gray-100 transition-all hover:shadow-2xl hover:shadow-white/20 transform hover:-translate-y-1 flex items-center gap-2 btn">
                Get 25 GB FREE - No Card Needed
              </a>

            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="mt-20 flex flex-wrap justify-center items-center gap-8 text-white/80 text-sm"
            >

            </motion.div>
          </motion.div>
        </div>
      </section>

      <footer className="Footer-section relative overflow-hidden bg-[#0F172A] text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
            {/* Brand Column */}
            <div className="col-span-2 md:col-span-4 lg:col-span-2">
              <div className="space-y-4 mr-12 padding-logo padding-top">
                <a href="https://spacebyte.in/" className="flex-shrink-0" aria-label="SpaceByte Homepage">
                  <img
                    src="/images/logo-light.png"
                    alt="Logo"
                    className="site-logo"
                  />
                </a>
                <div>
                  <p className="font-medium pt-8 pb-10">SPACEBYTE CLOUD STORAGE</p>
                  <p className="pb-10 pt-3 flex items-center gap-2">
                    311, Maruti Plaza, Sardar Patel Chowk, Krishnanagar, Ahmedabad, Gujarat,
                    India - 382345</p>
                  <p className="pb-10 pt-3 flex items-center gap-2">
                    Mo. +91 97257 71457
                  </p>
                </div>
                <div>
                  <p className="font-bold text-base pb-10 pt-3">FOLLOW US FOR "SPACEBYTE.IN"</p>
                  <div className="flex gap-4 mt-3 logg mb-10">
                    <ul className="flex space-x-16 pt-3 pr-10">
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.facebook.com/profile.php?id=61567307277313&mibextid=ZbWKwL">
                          <FacebookIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.instagram.com/spacebyte.in?igsh=MmZidnprMXZtbTho " aria-label="SpaceByte Instagram Profile">
                          <InstagramIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://youtube.com/@spacebytecloudstorage?si=lt2mfOM7kSaN3g6B" aria-label="SpaceByte YouTube Channel">
                          <YoutubeIcon className="text-white" />
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Column */}
            <div className='py-8 md:py-20 px-30'>
              <h2 className="font-semibold text-xl mb-10  text-start ">Product</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="#features" className="hover:text-white transition-colors text-start">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#Pricing" className="hover:text-white transition-colors">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#security" className="hover:text-white transition-colors">
                    Security
                  </a>
                </li>
                <li>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div className='py-8 md:py-20 px-30'>
              <h2 className="font-semibold text-xl mb-10  text-start">Company</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="/about-us" className="hover:text-white transition-colors">
                    About Us
                  </a>
                </li>
                {/* <li>
                  <a href="/pages/articles" className="hover:text-white transition-colors">
                    Articles
                  </a>
                </li> */}
                <li>
                  <a href="/contact" className="hover:text-white transition-colors">
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>

            {/* Resources Column */}
            <div className='py-8 md:py-20 px-30'>
              <h2 className="font-semibold text-xl mb-10  text-start">Resources</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="/pages/blog" className="hover:text-white transition-colors">
                    Blog
                  </a>
                </li>
                <li>
                  <a href="#faq" className="hover:text-white transition-colors">
                    FAQs
                  </a>
                </li>
                <li>
                  <a href="/contact" className="hover:text-white transition-colors">
                    Help Center
                  </a>
                </li>
                {/* <li>
                <a href="https://spacebyte.in/api-docs" className="hover:text-white transition-colors">
                  Developers
                </a>
              </li> */}
                <li>
                  <a href="/storage/sitemaps/sitemap-index.xml" className="hover:text-white transition-colors">
                    Sitemap
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal Column */}
            <div className='py-8 md:py-20 px-30'>
              <h3 className="font-semibold text-xl mb-10  text-start">Legal</h3>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="font-inter border-t border-t-primary/80 py-16 text-center mt-5">
            <p className="text-sm">
              Copyright © {new Date().getFullYear()}{' '}
              <a href="/login" className="color-primary-gradient">
                SpaceByte Cloud Storage
              </a>
              , All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
      </Fragment>
    );
  }

  function CalculateDialogTrigger() {
    const form = useForm<ContactPagePayload>({
      defaultValues: {
        message: '', // Initialize message field
      },
    });
    const [isOpen, setIsOpen] = useState(false);
    const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
    const submitForm = useSubmitContactForm(form);
    const { verify, isVerifying } = useRecaptcha('contact');

    return (
      <>
        <DialogTrigger type="modal" isOpen={isOpen} onOpenChange={setIsOpen}>
          <button className="btn w-full rounded-lg !text-white font-semibold
                  !bg-gradient-to-r !from-[#0072ff] !to-[#00c6ff]  shadow-lg
                  hover:shadow-xl transition">
            <span>Get Quote</span>
          </button>
          <Dialog className="w-full max-w-lg rounded-xl p-6">
            <DialogHeader
              className="font-archivo text-2xl font-semibold"
              showDivider
            >
              <Trans message="Storage Cost Calculator" />
            </DialogHeader>
            <DialogBody>
              <div className="font-inter flex items-center justify-center">
                Enter the storage size (whole number greater than **5 TB**) to
                calculate your monthly cost.
              </div>
              <Form
                form={form}
                className="mt-4 flex flex-col items-center text-center"
                onSubmit={async value => {
                  calculateCost(value);
                }}
              >
                <FormTextField
                  label={<Trans message="Enter Storage in TB" />}
                  name="tb"
                  required
                  type="number"
                  className="mb-6 w-full rounded-lg p-2"
                />
                <div className="text-red-500 text-md" id="errorMsg"></div>
                <div className="text-blue-500 text-md" id="resultOutput"></div>
                <div className="mt-6 flex w-full justify-between gap-4">
                  <button className="btn-warning mt-16" type="submit">
                    Calculate
                  </button>
                  <Button
                    color="positive"
                    variant="outline"
                    className="mt-16 rounded-lg"
                    type="button"
                    onClick={() => {
                      const resultOutput =
                        document.getElementById('resultOutput')?.textContent;
                      form.setValue('message', resultOutput || ''); // Set the value of message field
                      setIsOpen(false); // Close current dialog
                      setIsContactDialogOpen(true); // Open Contact Us dialog
                    }}
                  >
                    Contact Us
                  </Button>
                </div>
              </Form>
            </DialogBody>
          </Dialog>
        </DialogTrigger>

        {isContactDialogOpen && (
          <DialogTrigger
            type="modal"
            isOpen={isContactDialogOpen}
            onOpenChange={setIsContactDialogOpen}
          >
            <Dialog className="w-full max-w-lg rounded-xl p-6">
              <DialogHeader
                className="font-archivo text-2xl font-semibold"
                showDivider
              >
                <Trans message="Contact Us" />
              </DialogHeader>
              <DialogBody>
                {submitForm.isPending ? (
                  <FullPageLoader />
                ) : (
                  <Form
                    form={form}
                    className="flex flex-col gap-4"
                    onSubmit={async value => {
                      const isValid = await verify();
                      if (isValid) {
                        submitForm.mutate(value, {
                          onSuccess: () => {
                            setIsContactDialogOpen(false); // Close the dialog on success
                          },
                        });
                      }
                    }}
                  >
                    <FormTextField
                      label={<Trans message="Name" />}
                      name="name"
                      required
                      className="rounded-lg p-2"
                    />
                    <FormTextField
                      label={<Trans message="Email" />}
                      name="email"
                      required
                      type="email"
                      className="rounded-lg p-2"
                    />
                    <FormTextField
                      label={<Trans message="Mobile Number" />}
                      name="mobileNumber"
                      required
                      type="number"
                      className="rounded-lg p-2"
                    />
                    <FormTextField
                      label={<Trans message="Message" />}
                      name="message"
                      required
                      inputElementType="textarea"
                      className="rounded-lg p-2"
                      rows={5}
                    />

                    <div className="mt-4 flex w-full justify-between gap-4">
                      <Button
                        variant="flat"
                        onClick={() => setIsContactDialogOpen(false)}
                        className="rounded-lg px-4 py-2 text-lg"
                      >
                        <Trans message="Back" />
                      </Button>
                      <Button
                        type="submit"
                        variant="flat"
                        color="primary"
                        className="rounded-lg px-4 py-2 text-lg"
                      >
                        <Trans message="Send Message" />
                      </Button>
                    </div>
                  </Form>
                )}
              </DialogBody>
            </Dialog>
          </DialogTrigger>
        )}
      </>
    );
  }

  function calculateCost({ tb }: any) {
    const tbInput: any = tb;
    const errorMsg: any = document.getElementById('errorMsg');
    const resultOutput: any = document.getElementById('resultOutput');

    // Reset messages
    errorMsg.textContent = '';
    resultOutput.textContent = '';

    // Validation: Ensure input is a positive integer and greater than 5
    if (!/^\d+$/.test(tbInput)) {
      errorMsg.textContent = 'Error: Storage must be a whole number!';
      return;
    }

    const tbValue = parseInt(tbInput, 10);

    if (tbValue <= 5) {
      errorMsg.textContent = 'Error: Storage must be greater than 5 TB!';
      return;
    }

    // Conversion and cost calculation
    const gbStorage = tbValue * 1024; // Convert TB to GB
    const cost = gbStorage * 0.15; // Calculate cost

    // Display result
    resultOutput.innerHTML = `
        Storage: <strong>${tbValue.toLocaleString()} TB</strong><br>
        Total Cost: <strong>₹${cost.toFixed(2)}/month</strong><br>
        GST and Convenience Fee Extra**
    `;
  }

  function ReviewCard({
    item,
    isActive,
  }: {
    item: any;
    isActive: boolean;
  }) {
    const { name, role, avatar, review, accent, light, rating } = item;
    
    // Extract the first letter of the name
    const initial = name?.charAt(0).toUpperCase() || "U";

    return (
      <div
        className={[
          "relative flex-shrink-0 overflow-hidden m-10 rounded-[28px] border-[1.5px] p-[clamp(24px,3.5vw,42px)] transition-all duration-500 flex flex-col justify-between",
          isActive ? "translate-y-[-4px] scale-[1.015]" : "scale-[0.97]",
        ].join(" ")}
        style={{
          minWidth: "clamp(270px, 42vw, 450px)",
          maxWidth: "clamp(270px, 42vw, 450px)",
          background: isActive
            ? `linear-gradient(150deg, ${light} 0%, white 100%)`
            : `linear-gradient(150deg, ${light}88 0%, #f8fafc 100%)`,
          borderColor: isActive ? `${accent}50` : "#e2e8f0",
          boxShadow: isActive
            ? `0 24px 60px ${accent}1a, 0 4px 16px ${accent}10`
            : "0 2px 12px rgba(0,0,0,0.05)",
          opacity: isActive ? 1 : 0.68,
        }}
      >
        <div>
          {/* Rating Section */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <span key={i} style={{ color: i < rating ? accent : "#cbd5e1" }} className="text-[18px]">
                  ★
                </span>
              ))}
            </div>
            {/* Subtle Quote Mark */}
            <span className="font-serif text-[60px] leading-none opacity-10 absolute right-8 top-6" style={{ color: accent }}>
              “
            </span>
          </div>

          <p className="font-archivo text-[clamp(14px,1.15vw,16px)] font-medium leading-[1.7] mb-8 text-[#334155]">
            "{review}"
          </p>
        </div>

        <div className="flex items-center gap-10">
          {/* AVATAR / INITIAL BOX */}
          <div 
            className="flex h-50 w-50 shrink-0 items-center justify-center rounded-full border-2 font-archivo text-[20px] font-bold"
            style={{ 
              borderColor: `${accent}40`,
              background: isActive ? `${accent}15` : "#f1f5f9",
              color: accent 
            }}
          >
            {avatar ? (
              <img src={avatar} alt={name} className="h-full w-full object-cover rounded-full" />
            ) : (
              <span>{initial}</span> 
            )}
          </div>

          <div className="overflow-hidden ">
            <h4 className="truncate font-archivo text-[17px] font-bold text-[#0f172a] leading-tight">
              {name}
            </h4>
            <p className="truncate text-[13px] font-semibold" style={{ color: accent }}>
              {role}
            </p>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 h-[3px] rounded-bl-[28px] transition-all duration-[650ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
          style={{
            width: isActive ? "100%" : "0%",
            background: `linear-gradient(90deg, ${accent}, ${accent}44)`,
          }}
        />
      </div>
    );
  }


  
function WhyCard({
  item,
}: {
  item: {
    icon: LucideIcon;
    title: string;
    text: string;
  };
}) {
  const { icon: Icon, title, text } = item;

  return (
    <div
      className="
        relative overflow-hidden rounded-[16px] border
        p-[32px_28px]
        transition-all duration-500
        ease-[cubic-bezier(0.4,0,0.2,1)]
        hover:-translate-y-[6px]
        hover:shadow-[0_16px_40px_rgba(0,112,255,0.12)]
      "
      style={{
        background: "#ffffff",
        borderColor: "#e4edff",

        /* ✅ Top Gradient Line (Best Professional Way) */
        backgroundImage:
          "linear-gradient(90deg, #0070ff, #00c6ff)",
        backgroundSize: "100% 4px",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "top",
      }}
    >
      {/* Icon */}
      <div
        className="w-[50px] h-[50px] rounded-[12px] flex items-center justify-center text-[22px] mb-[18px]"
        style={{
          background: "#eaf3ff",
        }}
      >
        <Icon size={22} strokeWidth={2} className="text-[#0070ff]" />
      </div>

      {/* Title */}
      <h3 className="text-[16px] font-bold text-[#0d0d2b] mb-[10px]">
        {title}
      </h3>

      {/* Text */}
      <p className="text-[13.5px] text-[#7a8fa6] leading-[1.7]">
        {text}
      </p>
    </div>
  );
}